import SEO from '../SEO';
import { TrendingUp, Search, MapPin, BarChart3, Target, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import DomainChecker from '../DomainChecker';
import { Link } from 'react-router-dom';

function SEOArlington() {
  const services = [
    { icon: <MapPin className="h-8 w-8" />, title: "Local SEO", desc: "Dominate 'near me' searches in Arlington and surrounding areas" },
    { icon: <Search className="h-8 w-8" />, title: "Keyword Research", desc: "Target the exact terms Arlington customers are searching" },
    { icon: <Globe className="h-8 w-8" />, title: "On-Page SEO", desc: "Optimize your website structure, content, and meta tags" },
    { icon: <BarChart3 className="h-8 w-8" />, title: "Analytics & Reporting", desc: "Track your rankings, traffic, and conversions monthly" },
    { icon: <Target className="h-8 w-8" />, title: "Link Building", desc: "Build authority with quality backlinks from local sources" },
    { icon: <TrendingUp className="h-8 w-8" />, title: "Google Business", desc: "Optimize your Google Business Profile for local visibility" }
  ];

  const results = [
    { metric: "+312%", label: "Organic Traffic Increase", business: "Arlington Restaurant" },
    { metric: "#1", label: "Google Maps Ranking", business: "Local Contractor" },
    { metric: "+189%", label: "Lead Generation", business: "Dental Practice" },
    { metric: "47", label: "Keywords on Page 1", business: "Auto Dealership" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SEO Services Arlington TX | Local SEO Company | Web-TechServices"
        description="Arlington SEO services that deliver results. Local SEO, Google rankings, and digital marketing for Arlington Texas businesses. Free SEO audit!"
        keywords="SEO Arlington TX, Arlington SEO company, local SEO Arlington, SEO services Arlington Texas, Arlington search engine optimization, Google rankings Arlington"
        canonical="https://web-techservices.com/services/seo-arlington"
        ogImage="https://static.prod-images.emergentagent.com/jobs/3ffa77a1-0bda-46eb-9179-0a59cf5bf2e9/images/c04194c3ee83f307aa6bba1923b55c88dd1cff60d347aff35607e09c05cd25de.png"
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <span className="bg-blue-500/30 text-blue-200 px-4 py-1 rounded-full text-sm font-medium">Arlington's Top-Rated SEO Agency</span>
          <h1 className="text-4xl md:text-6xl font-bold mt-6 mb-6">
            Arlington SEO Services<br />That Deliver Results
          </h1>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl">
            Get found by more Arlington customers. Our proven SEO strategies help local businesses dominate search results and drive qualified traffic.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/ai-search">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-8" data-testid="seo-arlington-audit-cta">
                Free SEO Audit <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/book-consultation">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-bold px-8" data-testid="seo-arlington-consult-cta">
                Talk to SEO Expert
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {results.map((r, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-400">{r.metric}</div>
                <div className="text-sm text-slate-400">{r.label}</div>
                <div className="text-xs text-slate-500 mt-1">{r.business}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center mb-4 text-slate-900">Our Arlington SEO Services</h2>
        <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">Comprehensive SEO strategies designed for Arlington businesses</p>
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((s, i) => (
            <Card key={i} className="hover:shadow-lg transition-shadow border-slate-200">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                  {s.icon}
                </div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">{s.title}</h3>
                <p className="text-slate-600">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Keywords We Target */}
      <section className="bg-slate-50 border-y border-slate-200 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8 text-slate-900">Keywords We Help You Rank For</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Arlington web design', 'web design Arlington TX', 'SEO services Arlington',
              'Arlington digital marketing', 'website developer Arlington', 'local SEO Arlington Texas',
              'small business website Arlington', 'ecommerce Arlington TX', 'Arlington marketing agency'
            ].map((kw, i) => (
              <span key={i} className="bg-white px-4 py-2 rounded-full shadow-sm border border-slate-200 text-sm text-slate-700">
                "{kw}"
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-blue-700 to-blue-600 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Dominate Arlington Search Results?</h2>
          <p className="text-xl text-blue-100 mb-8">Get a free SEO audit and see exactly how to outrank your competitors.</p>
          <Link to="/ai-search">
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-bold" data-testid="seo-arlington-footer-audit-cta">
              Get Free SEO Audit
            </Button>
          </Link>
        </div>
      </section>

      {/* Domain Checker */}
      <section className="py-16 bg-slate-950">
        <div className="container mx-auto px-4 max-w-2xl">
          <DomainChecker headline="Claim Your Arlington Business Domain" subtitle="Get a keyword-rich domain to boost your SEO — starting at $15.99/year" compact={true} />
        </div>
      </section>

    </div>
  );
}

export default SEOArlington;
